import React, { Children } from 'react';
import { TargetIcon, ShieldCheckIcon, RocketIcon, ArrowRightIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export function HomeEnginePreview() {
  const systems = [{
    icon: TargetIcon,
    title: 'The Recruiting Engine',
    subtitle: 'Sourcing',
    description: "We don't rely on luck. We use a purpose-built system to source, screen, and qualify candidates from the cold market. We filter out the noise so you only talk to high-intent applicants who are ready to interview.",
    link: '/how-it-works#recruiting'
  }, {
    icon: ShieldCheckIcon,
    title: 'The Retention Engine',
    subtitle: 'Anchoring',
    description: 'Retention doesn\'t start at 90 days; it starts at the handshake. We don\'t just give you a manual; we actively partner with you to drive the "Commitment Conversation" and onboarding process. We help you build the accountability loops that bridge the gap between the job offer and Day 1, ensuring your new hires actually show up.',
    link: '/how-it-works#retention'
  }, {
    icon: RocketIcon,
    title: 'The Results Engine',
    subtitle: 'Production',
    description: 'This is our secret weapon. We put your rookies through our Live Sales School – immersive, structured training sessions delivered weekly. We handle the heavy lifting of initial training and buy-in so they hit the field ready to produce. We also provide active consulting to help you optimize their launch based on real performance data.',
    link: '/how-it-works#results',
    highlight: true
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section className="section-padding bg-white" data-id="element-437">
      <div className="container-custom" data-id="element-438">
        <motion.div className="max-w-3xl mx-auto text-center mb-12 md:mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} data-id="element-439">
          <h2 className="mb-4 md:mb-6 text-deep-blue" data-id="element-440">
            Three Systems. One Complete Framework.
          </h2>
          <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-441">
            We handle the finding, the filtering, and the foundational training.
            You lead and develop from there.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 max-w-6xl mx-auto mb-8 md:mb-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-442">
          {systems.map((system, index) => {
          const Icon = system.icon;
          return <motion.div key={index} className={`bg-white rounded p-5 md:p-6 lg:p-8 border-2 transition-all group shadow-md ${system.highlight ? 'border-mint bg-mint/5' : 'border-gray-200'}`} variants={itemVariants} whileHover={{
            scale: 1.02,
            transition: {
              duration: 0.2
            }
          }} data-id="element-443">
                <div className={`w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded flex items-center justify-center mb-4 md:mb-5 lg:mb-6 group-hover:scale-110 transition-transform bg-mint`} data-id="element-444">
                  <Icon className="h-5 w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 text-deep-blue" data-id="element-445" />
                </div>
                <div className="mb-2" data-id="element-446">
                  <div className="text-xs font-semibold text-mint uppercase tracking-wide mb-1" data-id="element-447">
                    {system.subtitle}
                  </div>
                  <h3 className="text-base md:text-lg lg:text-xl text-deep-blue" data-id="element-448">
                    {system.title}
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 md:mb-5 lg:mb-6 text-sm md:text-base" data-id="element-449">
                  {system.description}
                </p>
                <a href={system.link} className="inline-flex items-center text-mint font-semibold hover:text-mint-dark transition-colors text-sm md:text-base" data-id="element-450">
                  Learn More{' '}
                  <ArrowRightIcon className="ml-2 h-3 w-3 md:h-4 md:w-4" data-id="element-451" />
                </a>
              </motion.div>;
        })}
        </motion.div>

        <motion.div className="text-center" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.5
      }} data-id="element-452">
          <a href="/how-it-works" className="inline-block bg-deep-blue hover:bg-deep-blue-light text-white font-bold px-6 md:px-8 py-3 md:py-4 rounded transition-colors shadow-lg text-sm md:text-base" data-id="element-453">
            See the Complete System Breakdown →
          </a>
        </motion.div>
      </div>
    </section>;
}